// ============================================
// Session timeline strip
// ============================================
//
// Turns one grouped session into the geometry a compact overview bar needs.
// The shape of real race data drove every decision here: a Sassnitz session
// ran 3 h 52 m and contained 44 SECONDS of waveform across 16 records, six of
// which landed inside the final 103 seconds. So:
//
//   • coverage is drawn separately from the window — the gaps are "not
//     recorded", not "quiet water", and the strip must not imply otherwise;
//   • markers within a few pixels are CLUSTERED, because at that zoom a burst
//     collapses to one smear and per-tick clicking would be unusable exactly
//     where the interesting data is.
//
// Pure geometry in fractions of the window (0..1) so the component only
// multiplies by its measured width.

const SOURCE_HZ = 960;

/** Milliseconds of waveform a record holds (its stored window). */
export function recordWindowMs(rec) {
  const n = rec?.sampleCount || 0;
  return (n / SOURCE_HZ) * 1000;
}

/**
 * The span the strip covers: the session's recording interval when markers
 * exist (the truthful window — a session can start long before its first
 * impact), else the extent of the records themselves. Returns null when there
 * is nothing time-placeable (uptime-stamped records, empty session).
 */
export function sessionWindow(session) {
  if (!session) return null;
  const epoch = session.recordedIsEpoch || session.sessionStartIsEpoch;
  let t0 = null;
  let t1 = null;
  const grow = (a, b) => {
    if (a == null) return;
    if (t0 == null || a < t0) t0 = a;
    if (t1 == null || b > t1) t1 = b;
  };
  for (const iv of session.intervals || []) {
    grow(iv.startMs, iv.endMs ?? iv.startMs);
  }
  for (const rec of session.records || []) {
    if (!rec.timestampIsEpoch && epoch) continue;
    const start = rec.windowStartMs ?? rec.timestampMs;
    grow(start, start + recordWindowMs(rec));
  }
  if (t0 == null || t1 == null) return null;
  if (t1 <= t0) t1 = t0 + 1000; // a zero-width window would divide by zero
  return { t0, t1, spanMs: t1 - t0, isEpoch: !!epoch };
}

/** Merge overlapping spans, so shared coverage isn't counted twice. */
function mergeSpans(spans) {
  const sorted = [...spans].sort((a, b) => a.t0 - b.t0);
  const out = [];
  for (const s of sorted) {
    const last = out[out.length - 1];
    if (last && s.t0 <= last.t1) last.t1 = Math.max(last.t1, s.t1);
    else out.push({ ...s });
  }
  return out;
}

/**
 * Which slices of the window actually hold samples, plus the total. This is
 * the number that stops a reader treating the empty stretches as measured
 * quiet: "44 s recorded of 3 h 52 m".
 */
export function coverage(session, win) {
  if (!win) return { spans: [], recordedMs: 0, fraction: 0 };
  const spans = [];
  for (const rec of session.records || []) {
    const dur = recordWindowMs(rec);
    if (dur <= 0) continue;
    const start = rec.windowStartMs ?? rec.timestampMs;
    spans.push({ t0: start, t1: start + dur });
  }
  const merged = mergeSpans(spans);
  const recordedMs = merged.reduce((sum, s) => sum + (s.t1 - s.t0), 0);
  return {
    spans: merged.map((s) => ({
      x0: (s.t0 - win.t0) / win.spanMs,
      x1: (s.t1 - win.t0) / win.spanMs,
    })),
    recordedMs,
    fraction: recordedMs / win.spanMs,
  };
}

/**
 * Markers for one lane, clustered at the given pixel resolution.
 *
 * Each cluster reports its peak record (what a click opens and what sizes the
 * tick) and everything it swallowed (what the tooltip lists), so a burst stays
 * both visible and reachable instead of six ticks fighting for one pixel.
 */
export function laneMarkers(
  records,
  win,
  { widthPx = 800, clusterPx = 7 } = {}
) {
  if (!win || !records?.length) return [];
  const minGap = clusterPx / Math.max(widthPx, 1);
  const placed = records
    .filter((r) => r.timestampMs != null)
    .map((r) => ({ rec: r, x: (r.timestampMs - win.t0) / win.spanMs }))
    .filter((m) => m.x >= -0.001 && m.x <= 1.001)
    .sort((a, b) => a.x - b.x);

  const clusters = [];
  for (const m of placed) {
    const cur = clusters[clusters.length - 1];
    if (cur && m.x - cur.lastX <= minGap) {
      cur.items.push(m.rec);
      cur.lastX = m.x;
      if ((m.rec.peakMagG ?? 0) > (cur.peak.peakMagG ?? 0)) cur.peak = m.rec;
    } else {
      clusters.push({ x: m.x, lastX: m.x, items: [m.rec], peak: m.rec });
    }
  }
  return clusters.map((c) => ({
    // Centre the tick on the cluster rather than its first member, so a wide
    // burst doesn't sit lopsided against the records it represents.
    x: (c.x + c.lastX) / 2,
    count: c.items.length,
    peak: c.peak,
    peakG: c.peak?.peakMagG ?? 0,
    items: c.items,
  }));
}

/** Everything the strip renders, for one session. */
export function buildSessionTimeline(session, opts = {}) {
  const win = sessionWindow(session);
  if (!win) return null;
  const records = session.records || [];
  return {
    window: win,
    coverage: coverage(session, win),
    lanes: {
      impact: laneMarkers(
        records.filter((r) => r.band === "impact"),
        win,
        opts
      ),
      event: laneMarkers(
        records.filter((r) => r.band !== "impact"),
        win,
        opts
      ),
    },
    // Recording intervals as fractions — a session cut short (start with no
    // end) runs to the window edge, which is honest: we don't know when it
    // stopped.
    intervals: (session.intervals || []).map((iv) => ({
      x0: ((iv.startMs ?? win.t0) - win.t0) / win.spanMs,
      x1: ((iv.endMs ?? win.t1) - win.t0) / win.spanMs,
      openEnded: iv.endMs == null,
    })),
  };
}

/** Compact duration for the strip's caption: "3 h 52 m", "44 s", "1 m 20 s". */
export function fmtDuration(ms) {
  if (ms == null) return "—";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return s % 60 ? `${m} m ${s % 60} s` : `${m} m`;
  const h = Math.floor(m / 60);
  return m % 60 ? `${h} h ${m % 60} m` : `${h} h`;
}
